import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Box, Stack, Typography, styled } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { MRT_ColumnDef, MRT_Localization } from "material-react-table";
import { AppRoutes } from "@/routes/config";
import { Modal } from "@/components/elements/Modal";
import { Table } from "@/components/elements/Table";
import { Tag } from "@/components/elements/Tag";
import { Button } from "@/components/elements/Button";
import { ControlledPagination } from "@/components/elements/Pagination";
import { useSelectionTable } from "@/hooks/useSelectionTable";
import { UseVisibleReturn } from "@/hooks/useVisible";
import { IconChevronDown, IconClose, IconDownloadBlue, IconFilterList } from "@/assets/icons";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { BulkSyncLearnerFilterPanel } from "@/features/learner/components/learner-list/BulkSyncLearnerFilterPanel";
import {
  buildBulkSyncSearchParams,
  countBulkSyncFilter,
  EMPTY_BULK_SYNC_FILTER,
  loadBulkSyncFilter,
  persistBulkSyncFilter,
  TBulkSyncFilterForm,
} from "@/features/learner/components/learner-list/bulk-sync-filter";
import { IconSort } from "@/assets/icons/IconSort";
import { SORTING, TSorting } from "@/constants/sorting";
import { background, black, button, divider, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { buildImportRowFromPrepared, MAX_IMPORT_LEARNERS } from "@/features/learner/components/learner-bulk-import/bulk-import-mock";
import { calculateResidencePeriods } from "@/features/learner/utils/calculates";
import { useInitSetup } from "@/providers/InitSetupProvider";
import { ENROLLMENT_STATUS_COLOR, getEnrollmentStatusLabel, getGenderLabel, USER_TYPE } from "@/types/enum";
import { searchRsoLearnersApi } from "@/api/services/main/rso-learner";
import { prepareBulkImportApi } from "@/api/services/main/learner";
import {
  TSearchRsoLearnerOutputDto,
  TSearchRsoLearnerReqDto,
} from "@/api/services/main/rso-learner/dtos/search-rso-learner.dto";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { LEARNER_LIST_PER_PAGE, START_PAGE } from "@/constants/pagination";
import { DATE_FORMAT } from "@/constants/datetime";
import { dayjs } from "@/utils/dayjs";
import { showError } from "@/utils/error";
import { toast } from "@/utils/toast";

// visa 由来の日付 (ISO / 日付文字列) をテーブル表示用に yyyy/MM/dd へ統一する。空・不正値は "—"。
const formatCellDate = (value?: string | null): string => {
  if (!value) return "—";
  const d = dayjs(value);
  return d.isValid() ? d.format(DATE_FORMAT) : "—";
};

// 1列のみのソート状態 (field は visa 検索APIのソートキー)。null=未ソート。
type TSortState = { field: string; dir: TSorting } | null;

// ソートUI。field を渡した列はクリックでソート可 (未ソート→ASC→DESC→未ソート)。
// field 未指定の列は従来どおり表示のみ (フィルタアイコン / クリック不可)。
const SortHeader = ({
  label,
  field,
  sort,
  onSort,
}: {
  label: string;
  field?: string;
  sort?: TSortState;
  onSort?: (field: string) => void;
}) => {
  const active = !!field && sort?.field === field;
  const isAsc = active && sort?.dir === SORTING.ASC;
  const clickable = !!field && !!onSort;
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={0.75}
      onClick={clickable ? () => onSort?.(field) : undefined}
      sx={{ cursor: clickable ? "pointer" : "default", userSelect: "none" }}
    >
      <span>{label}</span>
      {/* ソート可能な列のみアイコンを表示する (未ソート=フィルタ, ソート中=方向アイコン)。ソート不可の列はアイコンなし。 */}
      {/* flexShrink:0 で狭い列(在籍状況等)でもアイコンが縮まないようにする。 */}
      {clickable &&
        (active ? (
          <IconSort style={{ flexShrink: 0, ...(isAsc ? { transform: "rotate(180deg)" } : {}) }} fontSize={18} />
        ) : (
          <IconFilterList style={{ flexShrink: 0 }} fontSize={18} color={black[80]} />
        ))}
    </Stack>
  );
};

type Props = {
  rsoModal: UseVisibleReturn;
};

export const BulkSyncLearnerModal: FC<Props> = ({ rsoModal }) => {
  const navigate = useNavigate();

  // 会社(company)アカウントは自社の外国人しか見ないため、所属機関のフィルタ条件・列を出さない。
  // RSO(登録支援機関)は複数の所属機関を横断して扱うため表示する。 (spec: STUDY_RSO_LeanerImport-01a)
  const { userType } = useInitSetup();
  const isCompanyUser = userType === USER_TYPE.COMPANY_USER;

  const [page, setPage] = useState(START_PAGE);
  // サーバソート状態 (1列のみ)。ヘッダークリックで設定し、params 経由で検索APIへ渡す。
  const [sort, setSort] = useState<TSortState>(null);
  // 取り込みボタン押下中 (prepare API 呼び出し中) はボタンをローディングにする。
  const [isImporting, setIsImporting] = useState(false);

  // 「フィルタ」パネル (Popover) のアンカー。null=閉じている。
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  // パネルで「条件を設定」した確定条件。localStorage に永続化し、再オープン/リロード後も復元する。
  const [filters, setFilters] = useState<TBulkSyncFilterForm>(() => loadBulkSyncFilter());
  // トリガーの「適用中フィルタを解除」確認モーダル。
  const [isClearFilterConfirmOpen, setIsClearFilterConfirmOpen] = useState(false);
  // 適用中の条件数 (日付レンジは1件換算)。0 のときはトグルボタン、1件以上で適用数バッジを表示する。
  const activeFilterCount = useMemo(() => countBulkSyncFilter(filters), [filters]);

  const handleClearAppliedFilter = () => {
    setFilters(EMPTY_BULK_SYNC_FILTER);
    persistBulkSyncFilter(EMPTY_BULK_SYNC_FILTER); // localStorage からも除去する
    setPage(START_PAGE);
    clearSelection();
    setIsClearFilterConfirmOpen(false);
  };

  // ヘッダークリックでソートをトグル: 未ソート→ASC→DESC→未ソート。ソート変更時は1ページ目へ戻す。
  const handleSort = useCallback((field: string) => {
    setSort((prev) => {
      if (prev?.field !== field) return { field, dir: SORTING.ASC };
      if (prev.dir === SORTING.ASC) return { field, dir: SORTING.DESC };
      return null;
    });
    setPage(START_PAGE);
  }, []);

  // クエリ条件: 「フィルタ」パネル(filters) の確定条件 + ページング + ソート。
  const params = useMemo<TSearchRsoLearnerReqDto>(() => {
    const p: TSearchRsoLearnerReqDto = { page, perPage: LEARNER_LIST_PER_PAGE };
    Object.assign(p, buildBulkSyncSearchParams(filters, isCompanyUser));
    if (sort) p.sorts = [`${sort.field}|${sort.dir}`];
    return p;
  }, [filters, page, isCompanyUser, sort]);

  const { data, isFetching, error } = useQuery({
    queryKey: [QUERY_KEYS.VISA.SEARCH_EMPLOYEES, isCompanyUser, params],
    queryFn: () => searchRsoLearnersApi(params, isCompanyUser),
    enabled: rsoModal.visible,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (error) showError(error);
  }, [error]);

  const rows = useMemo(() => data?.data ?? [], [data]);
  const pagination = data?.pagination;

  const columns = useMemo<MRT_ColumnDef<TSearchRsoLearnerOutputDto>[]>(() => {
    const cols: MRT_ColumnDef<TSearchRsoLearnerOutputDto>[] = [
      { header: "管理番号", id: "managementNumber", size: 110, Header: <SortHeader label="管理番号" field="managementNumber" sort={sort} onSort={handleSort} />, Cell: ({ row }) => row.original.managementNumber || "—" },
      {
        header: "在籍状況",
        id: "employmentStatus",
        size: 106,
        Header: <SortHeader label="在籍状況" field="employmentStatus" sort={sort} onSort={handleSort} />,
        Cell: ({ row }) => {
          // 在籍状況はキーで返るため日本語ラベルに変換し、対象の3状態のみタグ表示する。
          const label = getEnrollmentStatusLabel(row.original.employmentStatus);
          if (!label) return "—";
          return <Tag variant="light" hideDot solidBg color={ENROLLMENT_STATUS_COLOR[label]} title={label} minWidth="64px" />;
        },
      },
      {
        header: "所属機関",
        id: "companyName",
        size: 220,
        Header: <SortHeader label="所属機関" field="companyName" sort={sort} onSort={handleSort} />,
        // 220px に収まらない場合は末尾を3点リーダーで省略する (ホバー時のツールチップは不要なため表示しない)。
        Cell: ({ row }) => {
          const name = row.original.companyName;
          if (!name) return "—";
          return (
            <Box component="span" sx={{ display: "block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {name}
            </Box>
          );
        },
      },
      { header: "外国人名", id: "fullName", size: 200, Header: <SortHeader label="外国人名" field="fullName" sort={sort} onSort={handleSort} />, Cell: ({ row }) => row.original.fullName || "—" },
      { header: "分野", id: "industry", size: 130, Header: <SortHeader label="分野" field="specificIndustryName" sort={sort} onSort={handleSort} />, Cell: ({ row }) => row.original.specificIndustryName || "—" },
      { header: "国籍", id: "nationality", size: 96, Header: <SortHeader label="国籍" field="nationalityName" sort={sort} onSort={handleSort} />, Cell: ({ row }) => row.original.nationalityName || "—" },
      { header: "性別", id: "gender", size: 72, Header: <SortHeader label="性別" field="gender" sort={sort} onSort={handleSort} />, Cell: ({ row }) => getGenderLabel(row.original.gender) || "—" },
      { header: "入社日", id: "joinDate", size: 110, Header: <SortHeader label="入社日" field="hiringDate" sort={sort} onSort={handleSort} />, Cell: ({ row }) => formatCellDate(row.original.hiringDate) },
      { header: "在留期限", id: "residenceExpiryDate", size: 120, Header: <SortHeader label="在留期限" field="residenceExpiryDate" sort={sort} onSort={handleSort} />, Cell: ({ row }) => formatCellDate(row.original.residenceExpiryDate) },
      {
        header: "1号経過期間",
        id: "statusOneElapsedPeriod",
        size: 130,
        // 経過期間は表示値(elapsed)が client 計算のためソート対象外 (表示のみ)。
        Header: <SortHeader label="1号経過期間" />,
        // 1号起算日(skillOneStartDate) + ブランク期間(blankPeriodMonthly) から経過期間を自動計算する。
        // visa API の事前計算値(specificSkillOnePeriod)は古い/形式不一致のことがあるため使わない。
        Cell: ({ row }) => calculateResidencePeriods(row.original.skillOneStartDate, row.original.blankPeriodMonthly, "—").elapsed,
      },
    ];
    // 会社アカウントでは所属機関の列を除外する
    return cols.filter((col) => !(isCompanyUser && col.id === "companyName"));
  }, [isCompanyUser, sort, handleSort]);

  const { table, rowSelectionKeys, clearSelection } = useSelectionTable({
    primaryKey: "id",
    columns,
    rows,
    enableRowSelection: true,
    layoutMode: "grid",
    pinningColumnsLeft: ["mrt-row-select"],
    // 一度に取り込める人数を制限する。超える選択操作は無視してメッセージを表示する。
    maxSelection: MAX_IMPORT_LEARNERS,
    onExceedMaxSelection: (max) => toast.error(`一度に取り込める外国人は${max}名までです。${max}名以内で選択してください。`),
    localization: {
      toggleSelectAll: "すべて選択",
      toggleSelectRow: "選択",
    } as Partial<MRT_Localization> as MRT_Localization,
  });

  // 選択はページを跨いで保持されるが、取り込み時に参照できる rows は現在ページ分のみ。
  // 別ページで選んだ行も取り込めるよう、選択された行の完全なデータを id 毎にキャッシュする。
  const [selectedRowsById, setSelectedRowsById] = useState<Record<string, TSearchRsoLearnerOutputDto>>({});
  useEffect(() => {
    setSelectedRowsById((prev) => {
      const next = { ...prev };
      // 現在ページで選択中の行データを取り込む/最新化する。
      for (const row of rows) {
        const key = String(row.id);
        if (rowSelectionKeys.includes(key)) next[key] = row;
      }
      // 選択解除された id はキャッシュから除く。
      for (const key of Object.keys(next)) {
        if (!rowSelectionKeys.includes(key)) delete next[key];
      }
      return next;
    });
  }, [rows, rowSelectionKeys]);

  // 開いたら保存済みフィルタ(localStorage)を復元する。閉じたら選択・ページ・ソートのみリセットし、
  // フィルタは localStorage に永続化したまま残す (次回オープン/リロードで復元)。
  useEffect(() => {
    if (rsoModal.visible) {
      setFilters(loadBulkSyncFilter());
    } else {
      setPage(START_PAGE);
      setSort(null);
      setFilterAnchorEl(null);
      clearSelection();
    }
  }, [rsoModal.visible]);

  // フィルタ確定時は localStorage へ保存し、1ページ目へ戻して選択をクリアする。
  const handleApplyFilters = (next: TBulkSyncFilterForm) => {
    setFilters(next);
    persistBulkSyncFilter(next);
    setPage(START_PAGE);
    clearSelection();
  };

  const handleImport = async () => {
    // チェックした行のみを prepare API へ渡し、study マスタへのID解決(国籍/分野/所属機関/性別)を済ませてから取り込み画面へ遷移する。
    // ページを跨いで選んだ行も対象にするため、現在ページの rows ではなくキャッシュ(selectedRowsById)から構築する。
    const employees = Object.values(selectedRowsById)
      .map((row) => ({
        visaEmployeeId: row.id,
        fullName: row.fullName,
        managementNumber: row.managementNumber,
        employmentStatus: row.employmentStatus,
        companyName: row.companyName,
        specificIndustryName: row.specificIndustryName,
        nationalityName: row.nationalityName,
        gender: row.gender,
        hiringDate: row.hiringDate,
        residenceExpiryDate: row.residenceExpiryDate,
        specificSkillOnePeriod: row.specificSkillOnePeriod,
        province: row.province,
        municipality: row.municipality,
        skillOneStartDate: row.skillOneStartDate,
        blankPeriodMonthly: row.blankPeriodMonthly,
        company: row.company,
      }));

    try {
      setIsImporting(true);
      const prepared = await prepareBulkImportApi({ employees });
      const importRows = prepared.map(buildImportRowFromPrepared);
      rsoModal.close();
      navigate(AppRoutes.learnerBulkImport, { state: { rows: importRows, source: "visa" } });
    } catch (e) {
      showError(e);
    } finally {
      setIsImporting(false);
    }
  };

  const total = pagination?.total ?? 0;

  return (
    <Modal
      isOpen={rsoModal.visible}
      title="irohana visa から外国人を取り込み"
      width={1134}
      enableFooter={false}
      onClose={rsoModal.close}
    >
      <Stack gap={2} height="100%" mt={1}>
        <Stack direction="row" alignItems="center" gap={2} flexShrink={0}>
          <Typography variant="body14Semi" color={text.primary}>
            全 {total.toLocaleString("en-US")} 件中 {rowSelectionKeys.length} 件を選択
          </Typography>
          {/* 「フィルタ」トグル: クリックで詳細条件パネル(Popover)を開閉する。
              適用数 0 のときはトグルボタン、1件以上のときは適用数バッジ + 解除アイコンを表示する。 */}
          {activeFilterCount > 0 ? (
            <ActiveFilterButton>
              <Box
                sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                onClick={(e) => setFilterAnchorEl(e.currentTarget)}
              >
                <IconFilterList width={20} height={20} color={black[80]} />
                <Typography variant="cap12Bold" sx={{ mx: 1 }}>
                  フィルタ適用数:{" "}
                  <Typography component="span" variant="cap12Bold" sx={{ color: button.primary }}>
                    {String(activeFilterCount).padStart(2, "0")}
                  </Typography>
                </Typography>
              </Box>
              <Box
                component="span"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsClearFilterConfirmOpen(true);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  ml: 0.5,
                  "&:hover": { opacity: 0.7 },
                }}
              >
                <IconClose width={16} height={16} color={black[80]} />
              </Box>
            </ActiveFilterButton>
          ) : (
            <FilterButton
              onClick={(e: React.MouseEvent<HTMLElement>) => setFilterAnchorEl((prev) => (prev ? null : e.currentTarget))}
              data-open={Boolean(filterAnchorEl)}
            >
              <IconFilterList width={20} height={20} color={black[80]} />
              <Typography variant="cap12Bold" sx={{ color: text.primary, mx: 1 }}>
                フィルタ
              </Typography>
              <IconChevronDown
                width={20}
                height={20}
                color={black[80]}
                style={{
                  transition: "transform 0.2s ease",
                  transform: filterAnchorEl ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            </FilterButton>
          )}
        </Stack>

        <BulkSyncLearnerFilterPanel
          anchorEl={filterAnchorEl}
          onClose={() => setFilterAnchorEl(null)}
          onApply={handleApplyFilters}
          initialFilters={filters}
          isCompanyUser={isCompanyUser}
        />

        <ConfirmDeleteModal
          open={isClearFilterConfirmOpen}
          onClose={() => setIsClearFilterConfirmOpen(false)}
          onOk={handleClearAppliedFilter}
          message="適用中のフィルタを解除しますがよろしいですか？"
          title=""
        />

        {/* テーブルは行数ぶんの高さ(内容高)にし、最終行の下に余白が出ないようにする。
            画面が低いときだけ maxHeight でテーブル内スクロールに切り替える */}
        <Stack
          minHeight={0}
          sx={{
            "& > div": { backgroundColor: background.white },
            "& .MuiPaper-root": { backgroundColor: background.white },
          }}
        >
          <Table table={table} isLoading={isFetching} minHeight={150} maxHeight="calc(90vh - 212px)" />
        </Stack>

        <Stack direction="row" alignItems="center" justifyContent="space-between" flexShrink={0} mt={1}>
          {pagination && pagination.totalPage > 1 ? (
            <ControlledPagination totalPage={pagination.totalPage} page={page} onChangePage={setPage} />
          ) : (
            <span />
          )}
          <Stack direction="row" gap={2}>
            <Button variant="ghost" size="md" onClick={rsoModal.close}>
              <Typography variant="body14Semi">キャンセル</Typography>
            </Button>
            <Button
              size="md"
              isLoading={isImporting}
              disabled={rowSelectionKeys.length === 0 || isImporting}
              onClick={handleImport}
              startIcon={<IconDownloadBlue width={20} height={20} color={text.white} />}
            >
              <Typography variant="body14Semi">study に取り込み</Typography>
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Modal>
  );
};

const FilterButton = styled(Box)`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border: 1px solid ${divider.middle};
  border-radius: ${rounded.sm};
  cursor: pointer;
  &:hover,
  &[data-open="true"] {
    border-color: ${button.primary};
  }
`;

const ActiveFilterButton = styled(Box)`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border: 1px solid ${button.primary};
  border-radius: ${rounded.sm};
  background: #ecf9f9;
`;
